import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { CallEndReason } from "@/types/api";

type Reason = Exclude<CallEndReason, "">;

const REASON_KEYS: Reason[] = ["CUSTOMER_DISCONNECTED", "LOST_SIGNAL", "OTHER"];

interface EndCallDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: { reason: Reason; note: string }) => void;
  submitting: boolean;
}

/**
 * Confirms + captures why before ending a call early — for customers who
 * got cut off, lost signal, or whatever the reason was (see CallLog).
 * Doesn't reuse ConfirmDialog's "default-focus-Cancel" safety pattern:
 * that's for irreversible destructive actions (delete/deactivate), and
 * this isn't one — it's a routine call-center action a Staff member needs
 * to do quickly once a call has already dropped. Reason is still
 * required (matches the backend), just not defensively hard to reach.
 *
 * Part of FlowPlayerPage (Staff-facing, bilingual per user.language_preference
 * — see lib/i18n.ts), so unlike the admin-only Call Log page this one goes
 * through useTranslation rather than hardcoded English.
 */
export function EndCallDialog({ open, onClose, onConfirm, submitting }: EndCallDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<Reason | "">("");
  const [note, setNote] = useState("");

  // The dialog stays mounted (just hidden) between opens, so state
  // wouldn't otherwise reset on its own — start with a clean draft every
  // time it's reopened for a new call.
  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    onConfirm({ reason, note: note.trim() });
  };

  const reasonLabel = (key: Reason) =>
    ({
      CUSTOMER_DISCONNECTED: t("flowPlayer.endCallReasonCustomerDisconnected"),
      LOST_SIGNAL: t("flowPlayer.endCallReasonLostSignal"),
      OTHER: t("flowPlayer.endCallReasonOther"),
    })[key];

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("flowPlayer.endCallDialogTitle")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-slate-600">{t("flowPlayer.endCallDialogDescription")}</p>
          <div className="space-y-1.5">
            <Label>{t("flowPlayer.endCallReasonLabel")}</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as Reason)}>
              <SelectTrigger>
                <SelectValue placeholder={t("flowPlayer.endCallReasonPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {REASON_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {reasonLabel(key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("flowPlayer.endCallNoteLabel")}</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("flowPlayer.endCallNotePlaceholder")}
            />
            {/* RA 10173 (Data Privacy Act) — this note is stored indefinitely
             * and visible to any Admin reviewing the call log, so it shouldn't
             * become an unmonitored channel for customer PII. */}
            <p className="text-xs text-slate-400">{t("flowPlayer.endCallNotePiiWarning")}</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {t("flowPlayer.cancel")}
            </Button>
            <Button type="submit" variant="danger" disabled={!reason} loading={submitting}>
              {t("flowPlayer.endCall")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
